import React from "react";
import ReactDOM from "react-dom/client";
import { RecoilRoot } from "recoil";
import RecoilNexus from "recoil-nexus";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import App from "./App";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
import { initImpersonationFromUrl } from "./common/impersonation";
import { captureNaverAdParams } from "./common/adTracking";
import { captureGoogleAdUtm, getGoogleAdUtm } from "./v1/features/LeadForm/helpers/googleAd";
import { isPrerender, lazyWithPreload, startHydratingFromPrerender } from "./common/prerender";

// =============================================
// STATIC IMPORTS - 핵심 경로 (랜딩 + 인증 + 전환퍼널)
// =============================================
import LandingPage from "./v1/features/Landing/LandingPage";
import KakaoRedirect from "./pages/KakaoRedirect";
import { LoginPage } from "./pages/LoginPage";
import { SignInPage } from "./pages/SignInPage";
import {
  AppleCallback,
  CallbackLayout,
  GoogleCallback,
  KakaoCallback,
  NaverCallback,
} from "./pages/SocialCallback";
import { PaymentCallback } from "./pages/PaymentCallback";
import LegalConsultationPage from "./v1/features/PreData/pages/PreDataPage";
import PreDataLanding from "./v1/features/PreData/pages/PreDataLanding";
import CompanyPage from "./v1/features/Company/CompanyPage";
import LawyerDetailPage from "./v1/features/LawyerDetail/LawyerDetailPage";
import NotFoundPage from "./v1/features/NotFound/NotFoundPage";
import { Footer } from "./v1/ui/footer/Footer";
import { BottomRightLink } from "./components/BottomRightLink";

// 레거시 CSS: 동적 import로 render-blocking 방지 (랜딩페이지 FCP/LCP 개선)
import("primereact/resources/themes/saga-orange/theme.css");
import("primereact/resources/primereact.min.css");
import("react-responsive-modal/styles.css");

// =============================================
// LAZY IMPORTS - 방문 시에만 로드
// =============================================

// B2B
const B2bHome = React.lazy(() => import("./pages/b2b/B2bHome"));
const B2bLoginLanding = React.lazy(() => import("./pages/b2b/B2bLoginLanding"));
const B2bLogin = React.lazy(() => import("./pages/b2b/B2bLogin"));
const B2bSignup = React.lazy(() => import("./pages/b2b/B2bSignup"));
const B2bSignupWait = React.lazy(() => import("./pages/b2b/B2bSignupWait"));
const B2bPredata = React.lazy(() => import("./pages/b2b/B2bPredata"));
const B2bPredataWait = React.lazy(() => import("./pages/b2b/B2bPredataWait"));
const B2bPlanConfirm = React.lazy(() => import("./pages/b2b/B2bPlanConfirm"));
const B2bContractWait = React.lazy(() => import("./pages/b2b/B2bContractWait"));
const B2bSamchullyConsign = React.lazy(() => import("./pages/b2b/B2bSamchullyConsign"));
const CreditUnitAdd = React.lazy(() => import("./pages/b2b/creditAdd/CreditUnitAdd"));
const CreditList = React.lazy(() => import("./pages/b2b/creditManage/CreditList"));

// Social sign-up consent (pulls in antd, so kept out of the landing bundle)
const SocialAgreePage = React.lazy(() => import("./pages/SocialAgreePage"));

// StoryTelling (내부 데스크 도구)
const StoryTelling = React.lazy(() => import("./pages/storyTelling/StoryTelling"));
const StoryReRequest = React.lazy(() => import("./pages/storyTelling/StoryReRequest"));
const InfoTellingPage = React.lazy(() => import("./v1/features/InfoTelling/InfoTellingPage"));

// Enforcement
const EnforcementPage = React.lazy(() => import("./v1/features/Enforcement/pages/EnforcementPage"));
const EnforcementV2Page = React.lazy(() => import("./v1/features/EnforcementV2/pages/EnforcementV2Page"));

// 사건관리
const CaseDetailPage = React.lazy(() => import("./v1/features/CaseDetail/CaseDetailPage"));
const MyCmPage = React.lazy(() => import("./pages/MyCmPage"));
const CommonQuestionPage = React.lazy(() => import("./pages/CommonQuestionPage"));
const QuestionsPage = React.lazy(() => import("./pages/QuestionsPage"));
const OpRequestAnswerPage = React.lazy(() => import("./pages/OpRequestAnswerPage"));
const TodoPage = React.lazy(() => import("./pages/TodoPage"));
const AnnaePage = React.lazy(() => import("./pages/AnnaePage"));

// 전환퍼널 일부
const AnalyzePage = React.lazy(() => import("./v1/features/PreData/pages/AnalyzePage"));
const AfterLogin = React.lazy(() =>
  import("./v1/features/PreData/pages/AfterLogin").then(m => ({ default: m.AfterLogin }))
);
const FullSuePage = React.lazy(() => import("./v1/features/PreData/pages/FullSuePage"));

// 결제/채권
const FeePayment = React.lazy(() => import("./pages/FeePayment"));
const FeePaymentForSuccessReward = React.lazy(() => import("./pages/FeePaymentForSuccessReward"));
const CustomerPaymentPage = React.lazy(() => import("./pages/CustomerPaymentPage"));
const CloseInicis = React.lazy(() => import("./pages/PayClose"));
const PreBondPage = React.lazy(() => import("./pages/PreBondPage"));
const PreBondPrePage = React.lazy(() => import("./pages/PreBondPrePage"));

// 외부 랜딩
const DebtorChatLanding = React.lazy(() => import("./pages/DebtorChatLanding"));
const GasUnpaidView = React.lazy(() => import("./pages/GasUnpaidView"));
const SelfAdjustPage = React.lazy(() =>
  import("./v1/features/SelfAdjust/SelfAdjustPage")
);
const SelfAdjustEntry = React.lazy(() =>
  import("./v1/features/SelfAdjust/SelfAdjustEntry")
);
const CncityFinalNoti = React.lazy(() => import("./pages/etc/CncityFinalNoti"));

// 파트너
const PartnerRegistration = React.lazy(() => import("./v1/features/Partner/PartnerRegistration"));
const PartnerIntroduction = React.lazy(() => import("./v1/features/Partner/PartnerIntroduction"));

// 테스트
const TestPage = React.lazy(() => import("./pages/TestPage"));
const WageTestPage = React.lazy(() => import("./pages/WageTestPage"));

// Named export 페이지
const CreditorPage = React.lazy(() =>
  import("./pages/CreditorPage").then(m => ({ default: m.CreditorPage }))
);
const DebtorPage = React.lazy(() =>
  import("./pages/DebtorPage").then(m => ({ default: m.DebtorPage }))
);
const DisclaimerPage = React.lazy(() =>
  import("./pages/DisclaimerPage").then(m => ({ default: m.DisclaimerPage }))
);
const EditProfilePage = React.lazy(() =>
  import("./pages/EditProfilePage").then(m => ({ default: m.EditProfilePage }))
);
const EditUserPage = React.lazy(() =>
  import("./pages/EditUserPage").then(m => ({ default: m.EditUserPage }))
);
const FaqPage = React.lazy(() =>
  import("./pages/FaqPage").then(m => ({ default: m.FaqPage }))
);
const NoticePage = React.lazy(() =>
  import("./pages/NoticePage").then(m => ({ default: m.NoticePage }))
);
const NewsPage = React.lazy(() =>
  import("./pages/NewsPage").then(m => ({ default: m.NewsPage }))
);
// 사전 렌더 공개 경로라 수화 전에 미리 불러올 수 있게 만든다(아래 PRERENDERED_LAZY_PAGES)
const TermsPage = lazyWithPreload(() =>
  import("./pages/TermsPage").then(m => ({ default: m.TermsPage }))
);
const UserQuitPage = React.lazy(() =>
  import("./pages/UserQuitPage").then(m => ({ default: m.UserQuitPage }))
);
const QuitReasonPage = React.lazy(() =>
  import("./pages/QuitReasonPage").then(m => ({ default: m.QuitReasonPage }))
);
const QuitEndPage = React.lazy(() =>
  import("./pages/QuitEndPage").then(m => ({ default: m.QuitEndPage }))
);
// Privacy
const PrivacyPage = lazyWithPreload(() => import("./pages/privacy/PrivacyPage"));
const JoinPrivacyAgree = React.lazy(() =>
  import("./pages/privacy").then(m => ({ default: m.JoinPrivacyAgree }))
);
const CasePrivacyAgree = React.lazy(() =>
  import("./pages/privacy").then(m => ({ default: m.CasePrivacyAgree }))
);
const CasePrivacyOptionAgree = React.lazy(() =>
  import("./pages/privacy").then(m => ({ default: m.CasePrivacyOptionAgree }))
);
const MessagePrivacyAgree = React.lazy(() =>
  import("./pages/privacy").then(m => ({ default: m.MessagePrivacyAgree }))
);

const router = createBrowserRouter(
  createRoutesFromElements(
    // 기존의 Route를 createRoutesFormElements에 전달하기
    // callback류와 App류를 구분하여, Callback시 불필요한 splash 나오지 않도록 함.
    <Route>
      <Route element={<App />}>
        <Route path="/" element={<LandingPage />} />
        {/* 블로그 등 외부 유입 경유 라우트: 추적 + 기기별 분기(모바일→카톡, PC→안내모달) */}
        <Route path="/kakao" element={<KakaoRedirect />} />
        <Route path="/b2b" element={<B2bHome />} />
        <Route path="/b2bLoginLanding" element={<B2bLoginLanding />} />
        <Route path="/b2bLogin" element={<B2bLogin />} />
        <Route path="/b2bSignup" element={<B2bSignup />} />
        <Route path="/b2bSignupWait" element={<B2bSignupWait />} />
        <Route path="/b2bPredata" element={<B2bPredata />} />
        <Route path="/b2bPredataWait" element={<B2bPredataWait />} />
        <Route path="/b2bPlanConfirm" element={<B2bPlanConfirm />} />
        <Route path="/b2bContractWait" element={<B2bContractWait />} />
        <Route path="/cq/:cmId" element={<CommonQuestionPage />} />
        <Route
          path={"/b2b/samchully/consign"}
          element={<B2bSamchullyConsign />}
        />
        <Route path="/b2b/creditList" element={<CreditList />} />
        <Route path="/b2b/creditUnitAdd" element={<CreditUnitAdd />} />
        <Route
          path="/desk/storyTelling/:counselId"
          element={<StoryTelling />}
        />
        <Route path="/desk/storyTellingOp/:opCmId" element={<StoryTelling />} />
        <Route
          path="/desk/storyReRequest/:counselId"
          element={<StoryReRequest />}
        />
        <Route
          path="/desk/infoTelling/:counselId"
          element={<InfoTellingPage />}
        />
        <Route
          path="/desk/infoTelling/:counselId/:questionIndex"
          element={<InfoTellingPage />}
        />
        {/*채무자용 채팅방 랜딩+채팅방 */}
        <Route path="/dc/:chatId" element={<DebtorChatLanding />} />
        {/*가스요금 미납내역:  */}
        <Route path="/gv" element={<GasUnpaidView />} />
        <Route path="/gv/:chatId" element={<GasUnpaidView />} />

        {/* 채무자 셀프 분납 조정 (SMS 링크 진입) */}
        <Route path="/self-adjust" element={<SelfAdjustEntry />} />
        {/* 고객사별 진입 경로: 알림톡/문자 발송 시 고객사를 특정해 안내(조회 실패 시 해당 카톡 채널로 유도) */}
        <Route
          path="/self-adjust/samchully"
          element={<SelfAdjustEntry company="samchully" />}
        />
        <Route
          path="/self-adjust/cncity"
          element={<SelfAdjustEntry company="cncity" />}
        />
        <Route
          path="/self-adjust/skmagic"
          element={<SelfAdjustEntry company="skmagic" />}
        />
        <Route
          path="/self-adjust/carrier"
          element={<SelfAdjustEntry company="carrier" />}
        />
        <Route path="/self-adjust/:token" element={<SelfAdjustPage />} />

        {/* 씨엔씨티 - 내용증명 */}
        <Route path="/cncity/finalnoti" element={<CncityFinalNoti />} />
        

        <Route path="/feePayment" element={<FeePayment />} />
        <Route path="/sfee/:sfeeId" element={<FeePaymentForSuccessReward />} />
        {/* 실비(경유확인서·복대리비용 등) 고객 결제 — BO 추가비용 표에서 발급한 링크 */}
        <Route path="/pay/:payId" element={<CustomerPaymentPage />} />

        <Route path="/signin" element={<SignInPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/notices" element={<NoticePage />} />

        <Route path="/company" element={<CompanyPage />} />
        <Route path="/company/lawyers/:slug" element={<LawyerDetailPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy/:termIdx?" element={<PrivacyPage />} />
        <Route path="/agreement/join-privacy" element={<JoinPrivacyAgree />} />
        <Route path="/agreement/case-privacy" element={<CasePrivacyAgree />} />
        <Route
          path="/agreement/case-privacy-option"
          element={<CasePrivacyOptionAgree />}
        />
        <Route
          path="/agreement/message-privacy"
          element={<MessagePrivacyAgree />}
        />

        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/profile" element={<EditProfilePage />} />
        <Route path="/edituser" element={<EditUserPage />} />
        <Route path="/social-agree" element={<SocialAgreePage />} />
        <Route path="/creditor/:chatId" element={<CreditorPage />} />
        <Route path="/debtor/:chatId" element={<DebtorPage />} />
        <Route path="/quit" element={<UserQuitPage />} />
        <Route path="/qreason" element={<QuitReasonPage />} />
        <Route path="/qend" element={<QuitEndPage />} />
        {/* payment/:charId 안 씀*/}
        <Route path="/pbs/:preBondId?" element={<PreBondPage />} />
        {/* 계산기에서는 필요없는 플로우. 카톡으로 들어오는 경우는 사용 */}
        <Route path="/pbs/pre/:preBondId?" element={<PreBondPrePage />} />
        <Route path="/payclose" element={<CloseInicis />} />
        <Route
          path="/chatlist"
          element={
            <div className="desktop:max-w-[1200px] mx-auto">
              <MyCmPage />
            </div>
          }
        />
        <Route path="/chatlist/:caseId" element={<CaseDetailPage />} />
        <Route
          path="/chatlist/:caseId/questions"
          element={<QuestionsPage />}
        />
        <Route
          path="/chatlist/:cmId/opRequest"
          element={<OpRequestAnswerPage />}
        />
        <Route path="/chatlist/:caseId/request" element={<TodoPage />} />
        <Route path="/chatlist/:caseId/annae" element={<AnnaePage />} />
        <Route path="/marketing-landing" element={<PreDataLanding />} />
        <Route
          path="/get-predata"
          element={
            <>
              <LegalConsultationPage />
              <Footer />
              <BottomRightLink />
            </>
          }
        />
        <Route
          path="/analyze"
          element={
            <>
              <AnalyzePage />
              <Footer />
            </>
          }
        />
        <Route path="/predata-login" element={<AfterLogin />} />
        <Route path="/full-sue" element={<FullSuePage />} />
        <Route
          path="/enforcement/:cmId"
          element={
            <>
              <EnforcementPage />
              <Footer />
            </>
          }
        />
        {/* 강제집행 v2: 목업처럼 전체 화면 사용 — 헤더(HIDE_NAVBAR_PATHS)·푸터 없음 */}
        <Route path="/enforcement2/:cmId" element={<EnforcementV2Page />} />
        <Route path="/test-info" element={<TestPage />} />
        <Route path="/wage-test" element={<WageTestPage />} />
        {/* 없는 주소: 맨 끝에 둔다(SEO 0-3). 서버도 같은 주소에 404 를 준다 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route element={<CallbackLayout />}>
        {" "}
        {/* Callback Layout */}
        <Route path="/partner/registration" element={<PartnerRegistration />} />
        <Route path="/partner/introduction" element={<PartnerIntroduction />} />
        <Route path="/callback_kakao" element={<KakaoCallback />} />
        <Route path="/callback_naver" element={<NaverCallback />} />
        <Route path="/callback_google" element={<GoogleCallback />} />
        <Route path="/callback_apple" element={<AppleCallback />} />
        <Route path="/paid/:oid" element={<PaymentCallback />} />
      </Route>
    </Route>,
  ),
);

// [고객화면 대리열람] BO가 iframe URL(?impersonationToken=...&readOnly=1)로 주입한 대리 토큰을
// 최초 렌더/최초 API 호출 이전에 파싱해 둔다. (useAxios 가 이 세션을 참조)
initImpersonationFromUrl();

// 네이버 광고 꼬리표(?n_keyword=...)를 첫 화면 주소에서 메모해 둔다 (GA4 키워드 성과 측정용)
captureNaverAdParams();

// Pretendard webfont bundled from npm (same origin, /static/media) instead of the jsdelivr CDN.
// Loaded as a separate CSS chunk after the page load event, like the old async CDN stylesheet, so the
// woff2 files never compete with the first paint. Skipped while prerendering so snapshots carry no link.
const loadPretendard = () =>
  import("pretendard/dist/web/variable/pretendardvariable-dynamic-subset.css");
if (!isPrerender()) {
  if (document.readyState === "complete") loadPretendard();
  else window.addEventListener("load", loadPretendard, { once: true });
}

// 구글 광고 꼬리표(?utm_source=google...)를 첫 화면 주소에서 메모해 둔다 (랜딩 상담 신청 폼 노출용)
captureGoogleAdUtm();

// 자꾸 2번 불려서<React.StrictMode> 제거함.
const tree = (
  <React.StrictMode>
    <HelmetProvider>
      <RecoilRoot>
        <RecoilNexus /> {/* RecoilNext to use recoil outside react component */}
        <RouterProvider router={router} fallbackElement={<div>Loading...</div>} />
      </RecoilRoot>
    </HelmetProvider>
  </React.StrictMode>
);

const container = document.getElementById("root");

// [SEO 4단계] 서버가 사전 렌더 HTML(scripts/prerender.mjs)을 준 공개 경로는 그 DOM을 지우지 않고 이어받는다(hydrate).
// 교체가 없으니 LCP 가 사전 렌더 화면이 그려진 시점에 고정된다. 첫 렌더가 저장본과 달라도 React 가 그 부분을
// 클라이언트 렌더로 복구하므로(=지금과 같은 결과) 기능은 깨지지 않는다.
// 광고·AI 유입 방문자(리드폼·하단 바가 붙어 화면 구조가 다름)와 sm. 도메인은 기존처럼 새로 그린다.
const normalizePath = (p) => (p.length > 1 ? p.replace(/\/+$/, "") : p);
const prerenderedPath = container.getAttribute("data-prerendered");
const shouldHydrate =
  prerenderedPath !== null &&
  normalizePath(prerenderedPath) === normalizePath(window.location.pathname) &&
  !window.location.host.startsWith("sm.") &&
  getGoogleAdUtm() === null;

// 사전 렌더 공개 경로 중 화면 코드가 lazy 인 곳: 코드를 먼저 받고 수화한다(기다리는 동안 화면은 이미 그려져 있음)
const PRERENDERED_LAZY_PAGES = { "/terms": TermsPage, "/privacy": PrivacyPage };

if (shouldHydrate) {
  const hydrate = () => {
    startHydratingFromPrerender();
    ReactDOM.hydrateRoot(container, tree, {
      onRecoverableError: (error) => {
        const message = String(error?.message ?? error).slice(0, 200);
        console.warn(`[hydrate] ${window.location.pathname} ${message}`);
      },
    });
  };
  const lazyPage = PRERENDERED_LAZY_PAGES[normalizePath(window.location.pathname)];
  // 코드 받기에 실패해도 수화는 한다(그때는 React.lazy 가 다시 받는다)
  if (lazyPage) lazyPage.preload().then(hydrate, hydrate);
  else hydrate();
} else {
  ReactDOM.createRoot(container).render(tree);
}
